/**
 * MemorySnapPage — /event/:slug/memory-snap
 *
 * Full-screen cinematic capture experience. Per brand lock:
 *   • dark radial background
 *   • Georgia serif headlines with gold accent text-shadow
 *   • NO browser feel, NO harsh UI
 *
 * Flow:
 *   1) live <video> preview with framing guides
 *   2) tap "Capture" → freeze frame
 *   3) transition into polaroid editor (photo + handwritten message)
 *   4) save → "✨ Added to the Memory Wall" → mini-animation → wall
 *
 * Manual capture only (Craig: "DO NOT auto-capture" — emotional pacing).
 * Pen palette is curated (10 colours), default seeded from event_type.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { brandToast } from '../lib/brandToast';
import { Camera, ArrowsCounterClockwise, DownloadSimple, ArrowRight, ShareNetwork } from '@phosphor-icons/react';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import { markCameraActive } from '../hooks/useEventCachePrefetch';
import { useBoothCameraPipeline } from '../hooks/useBoothCameraPipeline';
import ReconnectingScreen from '../components/ReconnectingScreen';
import BackButton from '../components/BackButton';
import Polaroid from '../components/Polaroid';
import { PEN_COLORS, defaultsForTheme, MESSAGE_PROMPTS, findPen } from '../lib/penColors';
import { downloadPolaroid, exportPolaroidPng } from '../lib/polaroidExport';
import { shareMemorySnap } from '../lib/shareMemorySnap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_MESSAGE = 120;

// Deterministic seed per session → same imperfections every render of
// this guest's snap (matches server-side persistence).
const makeSeed = () => Math.floor(Math.random() * 9_999_999) + 1;

const MemorySnapPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { event, status, retry } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();

  // Stages: 'live' → 'polaroid' → 'saving' → 'success'
  const [stage, setStage] = useState('live');
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [message, setMessage] = useState('');
  const [penId, setPenId] = useState('gold');
  const [seed] = useState(() => makeSeed());
  const [savedSnap, setSavedSnap] = useState(null);
  // "My snaps" strip — surfaces the guest's own contributions on the
  // success screen so emotional ownership lands before they leave.
  const [mySnaps, setMySnaps] = useState([]);
  const [sharing, setSharing] = useState(false);

  // iter204 — Phase B migration. Camera acquisition delegated to the
  // unified pipeline. Profile 'portrait' = 3:4, mirror-preview-only,
  // front-facing, audio off. The polaroid composition canvas below
  // (1080×1350) is the production export — kept untouched as
  // emotional payload per the Lens migration boundary.
  const cam = useBoothCameraPipeline({ profile: 'portrait' });
  const videoRef = cam.videoRef;
  const facingMode = cam.facingMode;

  // Responsive polaroid sizing — the Polaroid component renders at
  // an exact pixel `width` (tape, padding, message size all scale
  // off it). On narrow viewports (≤320px Android, foldables, split
  // screens) a hard-coded 260px overflowed the wrapper and the
  // "Wall" of the success h1 wrapped on top of the polaroid frame.
  // Clamp to viewport so the polaroid never crashes into the
  // heading or extends off-screen. Tracked on resize for foldables.
  const [polaroidWidth, setPolaroidWidth] = useState(() => {
    if (typeof window === 'undefined') return 240;
    return Math.min(260, Math.max(180, Math.floor(window.innerWidth * 0.74)));
  });
  useEffect(() => {
    const onResize = () => {
      setPolaroidWidth(Math.min(260, Math.max(180, Math.floor(window.innerWidth * 0.74))));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const captureCanvasRef = useRef(null);

  const prompt = useMemo(() => MESSAGE_PROMPTS[Math.floor(Math.random() * MESSAGE_PROMPTS.length)], []);
  const themeDefaults = useMemo(() => defaultsForTheme(event?.event_type), [event?.event_type]);
  useEffect(() => { if (themeDefaults?.[0]) setPenId(themeDefaults[0]); }, [themeDefaults]);

  // ── Camera lifecycle ──────────────────────────────────────────────
  // iter204 — Pipeline-owned. start()/stop() handle acquisition,
  // permission errors (with profile-appropriate copy), interruption
  // recovery, and dimensions stabilisation. The brand toast for
  // permission errors is bridged through the pipeline's onError.
  useEffect(() => {
    if (stage === 'live') {
      cam.start();
      markCameraActive(true);
    } else {
      cam.stop();
      markCameraActive(false);
    }
    return () => { cam.stop(); markCameraActive(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Bridge camera errors into the brand toast voice — preserves the
  // iter171 brand-aligned messaging.
  useEffect(() => {
    if (!cam.error) return;
    brandToast.slipped(
      cam.status === 'denied'
        ? 'Camera is asleep · allow access to take your snap'
        : 'The camera is taking a moment · please try again',
      { duration: 6000 },
    );
  }, [cam.error, cam.status]);

  // Fire analytics — guest started the flow.
  useEffect(() => {
    if (!event?.event_id) return;
    axios.post(`${API}/events/${event.event_id}/memory-snaps/track`, { type: 'memory_snap_started' }).catch(() => {});
  }, [event?.event_id]);

  // ── Capture ──────────────────────────────────────────────────────
  // Iter171 P0 rendering pass: shutter must feel instant.
  //
  // Previous flow used canvas.toDataURL('image/jpeg', 0.9) which is
  // synchronous and blocking — on a desktop with a 1080p webcam this
  // froze the UI for 100–400ms between the shutter tap and the
  // polaroid transition. Now:
  //   1. drawImage runs synchronously (cheap — single composite op)
  //   2. We transition to 'polaroid' immediately, using the canvas
  //      itself as the preview surface
  //   3. canvas.toBlob runs asynchronously in the background; the
  //      blob is held in a ref and shipped on Save
  //
  // JPEG quality also dropped from 0.9 → 0.82 — visually identical on
  // a 1080×1350 polaroid but ~40% smaller and ~30% faster to encode.
  const snapBlobRef = useRef(null);
  const handleCapture = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = captureCanvasRef.current || document.createElement('canvas');
    captureCanvasRef.current = canvas;
    canvas.width = 1080;
    canvas.height = 1350;
    const TARGET_RATIO = 0.8; // width / height
    const vW = v.videoWidth;
    const vH = v.videoHeight;
    const videoRatio = vW / vH;
    let sx, sy, sW, sH;
    if (videoRatio > TARGET_RATIO) {
      sH = vH;
      sW = Math.round(vH * TARGET_RATIO);
      sx = Math.round((vW - sW) / 2);
      sy = 0;
    } else {
      sW = vW;
      sH = Math.round(vW / TARGET_RATIO);
      sx = 0;
      sy = Math.round((vH - sH) / 2);
    }
    const ctx = canvas.getContext('2d', { alpha: false });
    if (facingMode === 'user') { ctx.save(); ctx.scale(-1, 1); ctx.translate(-canvas.width, 0); }
    ctx.drawImage(v, sx, sy, sW, sH, 0, 0, canvas.width, canvas.height);
    if (facingMode === 'user') ctx.restore();

    // Show the polaroid preview INSTANTLY — we use a lightweight
    // dataURL only for the visual preview. The actual upload blob is
    // encoded asynchronously below.
    setPhotoDataUrl(canvas.toDataURL('image/jpeg', 0.6));
    cam.stop();
    setStage('polaroid');

    // Encode the high-quality blob in the background; held in a ref
    // so Save can ship it without re-encoding.
    snapBlobRef.current = null;
    try {
      canvas.toBlob(
        (blob) => { snapBlobRef.current = blob; },
        'image/jpeg',
        0.82,
      );
    } catch { /* unsupported — fall back to dataURL round-trip on Save */ }
  };

  const handleRetake = () => {
    setPhotoDataUrl(null);
    setStage('live');
  };

  const handleFlip = () => {
    cam.switchCamera();
  };

  // ── Save ──────────────────────────────────────────────────────────
  // Iter171: prefer the pre-encoded blob from handleCapture (assembled
  // asynchronously in the background while the polaroid was shown).
  // If it isn't ready yet (rare — toBlob typically finishes before the
  // user finishes writing their message), wait briefly; otherwise
  // fall back to the dataURL round-trip path.
  const handleSave = async () => {
    if (!event?.event_id || !photoDataUrl) return;
    setStage('saving');
    try {
      let blob = snapBlobRef.current;
      if (!blob) {
        // Wait up to 1500ms for the background encode, otherwise
        // fall back to converting the preview dataURL.
        const start = performance.now();
        while (!snapBlobRef.current && performance.now() - start < 1500) {
          await new Promise((r) => setTimeout(r, 50));
        }
        blob = snapBlobRef.current;
        if (!blob) {
          const res = await fetch(photoDataUrl);
          blob = await res.blob();
        }
      }
      const form = new FormData();
      form.append('file', blob, `memory-snap-${Date.now()}.jpg`);
      form.append('message', message.slice(0, MAX_MESSAGE));
      form.append('pen_color', penId);
      form.append('style_seed', String(seed));
      const guestId = (typeof window !== 'undefined' && window.localStorage.getItem('eventdrop_guest_id')) || '';
      if (guestId) form.append('guest_id', guestId);
      const r = await axios.post(`${API}/events/${event.event_id}/memory-snaps`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const snap = r.data;
      if (snap.photo_url && !snap.photo_url.startsWith('http')) snap.photo_url = `${BACKEND_URL}${snap.photo_url}`;
      setSavedSnap(snap);
      setStage('success');

      // ── Dual-image save (Craig's 2026-05-13 evening lock) ────────
      // After the original lands, render the polaroid composite
      // (frame + handwritten caption) and upload it as the
      // authored companion. The composite carries `companion_of`
      // = original.snap_id so archive / export logic recognises
      // them as one authored moment. Failure here NEVER blocks
      // the success state — the original is already saved.
      try {
        const compositeBlob = await exportPolaroidPng({
          photoUrl: snap.photo_url,
          message: snap.message,
          penColor: snap.pen_hex,
          seed: snap.style_seed,
        });
        if (compositeBlob) {
          const companionForm = new FormData();
          companionForm.append('file', compositeBlob, `polaroid-${snap.snap_id}.png`);
          companionForm.append('message', snap.message || '');
          companionForm.append('pen_color', snap.pen_color || penId);
          companionForm.append('style_seed', String(snap.style_seed || seed));
          companionForm.append('companion_of', snap.snap_id);
          companionForm.append('kind', 'polaroid_render');
          if (guestId) companionForm.append('guest_id', guestId);
          // Fire-and-forget — the original snap success UX must
          // never wait on the companion render to complete.
          axios.post(`${API}/events/${event.event_id}/memory-snaps`, companionForm, {
            headers: { 'Content-Type': 'multipart/form-data' },
          }).catch(() => { /* swallow — companion is additive */ });
        }
      } catch (err) {
        // The original snap is already saved; the polaroid
        // composite is additive. Swallow and continue.
      }

      // Refresh "my snaps" strip — quiet emotional ownership signal.
      if (guestId) {
        axios.get(`${API}/events/${event.event_id}/memory-snaps?guest_id=${encodeURIComponent(guestId)}&limit=6`)
          .then((r) => {
            const list = (r.data?.snaps || []).map((s) => ({
              ...s,
              photo_url: s.photo_url && !s.photo_url.startsWith('http') ? `${BACKEND_URL}${s.photo_url}` : s.photo_url,
            }));
            setMySnaps(list);
          })
          .catch(() => {});
      } else {
        setMySnaps([snap]);
      }
      // Telemetry
      axios.post(`${API}/events/${event.event_id}/memory-snaps/track`, {
        type: 'memory_snap_completed',
        snap_id: snap.snap_id,
      }).catch(() => {});
    } catch (e) {
      // iter181 — surface the actual failure so prod reports can be
      // diagnosed in seconds. Server error message wins (it carries
      // domain context); fall back to HTTP status, then any axios
      // code (ECONNABORTED / Network Error), then the raw message.
      const detail =
        e?.response?.data?.detail
        || (e?.response?.status ? `Save failed · code ${e.response.status}` : '')
        || e?.code
        || e?.message
        || '';
      console.error('[MemorySnap] save failed', detail, e);
      brandToast.slipped(
        detail
          ? `A memory slipped away · ${detail}`
          : 'A memory slipped away · please try again',
        { duration: 6500 },
      );
      setStage('polaroid');
    }
  };

  // ── Status guards ────────────────────────────────────────────────
  if (status === 'failed') return <ReconnectingScreen status="failed" onRetry={retry} slug={slug} activeEvent={activeEvent} dashboardHref="/" />;
  if (!event) return <ReconnectingScreen status={status === 'restoring' ? 'restoring' : 'reconnecting'} slug={slug} activeEvent={activeEvent} />;

  const pen = findPen(penId);

  // Polished download — high-DPI PNG, branded filename, soft toast.
  const handleDownload = async () => {
    if (!savedSnap) return;
    const res = await downloadPolaroid(savedSnap, { event, filename: undefined });
    if (res?.ok) {
      brandToast.gentle('Polaroid saved to your photos', { duration: 3000 });
    } else {
      brandToast.slipped('That polaroid couldn\'t be saved · please try again', { duration: 5000 });
    }
  };

  // System share sheet (when supported) → Instagram / Messages / etc.
  // Falls back to clipboard with the same guest-authored caption.
  const handleShare = async () => {
    if (!savedSnap || sharing) return;
    setSharing(true);
    try {
      const res = await shareMemorySnap({ snap: savedSnap, event });
      if (res?.method === 'native') {
        brandToast.gentle('Memory shared', { duration: 2500 });
      } else if (res?.method === 'clipboard') {
        brandToast.gentle('Caption copied · paste it anywhere', { duration: 3500 });
      } else if (res?.method === 'opened') {
        brandToast.gentle('Opened the Memory Wall', { duration: 2500 });
      } else if (res?.method !== 'cancelled') {
        brandToast.slipped('The share sheet didn\'t open · try again', { duration: 4000 });
      }
    } finally {
      setSharing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div
      data-testid="memory-snap-page"
      style={{
        position: 'fixed', inset: 0, overflow: 'hidden',
        overflowX: 'hidden',
        background: 'radial-gradient(circle at top, #1a1a2e 0%, #0F1014 70%)',
        color: '#fff',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {/* Top bar — back + see-the-wall */}
      <div style={{ position: 'absolute', top: 18, left: 18, zIndex: 30, paddingTop: 'env(safe-area-inset-top)' }}>
        <BackButton fallbackHref={`/event/${slug}`} label="Back" variant="subtle" testId="memory-snap-back-btn" />
      </div>
      <button
        type="button"
        onClick={() => navigate(`/event/${slug}/memory-wall`)}
        data-testid="see-wall-btn"
        style={{
          position: 'absolute', top: 18, right: 18, zIndex: 30,
          paddingTop: 'env(safe-area-inset-top)',
          background: 'rgba(18,18,28,0.6)', border: '1px solid rgba(212,175,94,0.18)',
          color: 'rgba(255,255,255,0.92)', borderRadius: 999,
          height: 42, padding: '0 16px', fontSize: 13, fontWeight: 600,
          letterSpacing: '0.01em', backdropFilter: 'blur(18px)', cursor: 'pointer',
        }}
      >See the Wall →</button>

      {/* Header — hidden on SUCCESS stage; the success view renders
          its own inline header (in normal flow) so it no longer
          collides with the Polaroid composition on narrow viewports. */}
      {stage !== 'success' && (
        <div style={{ position: 'absolute', top: 80, left: 0, right: 0, textAlign: 'center', zIndex: 10, padding: '0 24px' }}>
          <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.32em', textTransform: 'uppercase', color: 'rgba(212,175,94,0.78)', marginBottom: 8 }}>✦ Memory Snap</p>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 500, letterSpacing: '-0.02em', textShadow: '0 0 22px rgba(212,175,94,0.28)', margin: 0 }}>
            {stage === 'live' && 'Take a selfie'}
            {stage === 'polaroid' && 'Leave a message'}
            {stage === 'saving' && 'Adding to the wall…'}
          </h1>
          <p style={{ marginTop: 6, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
            {stage === 'live' && 'This will become part of the Memory Wall'}
            {stage === 'polaroid' && 'Write a short note underneath the photo'}
          </p>
        </div>
      )}

      {/* STAGE: LIVE */}
      {stage === 'live' && (
        <>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 'min(86vw, 380px)', aspectRatio: '4/5', borderRadius: 18, overflow: 'hidden', boxShadow: '0 18px 60px rgba(0,0,0,0.6), 0 0 28px rgba(212,175,94,0.12)' }}>
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                style={{ width: '100%', height: '100%', objectFit: 'cover', ...cam.previewStyle }}
              />
            </div>
          </div>
          <div style={{ position: 'absolute', bottom: 'calc(36px + env(safe-area-inset-bottom))', left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 18, zIndex: 20 }}>
            <button
              type="button"
              onClick={handleFlip}
              data-testid="flip-cam-btn"
              aria-label="Flip camera"
              style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(18,18,28,0.7)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', backdropFilter: 'blur(14px)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            ><ArrowsCounterClockwise size={22} weight="bold" /></button>
            <button
              type="button"
              onClick={handleCapture}
              data-testid="capture-btn"
              aria-label="Capture"
              style={{ width: 78, height: 78, borderRadius: '50%', background: '#FFFFFF', border: '4px solid rgba(212,175,94,0.55)', boxShadow: '0 8px 26px rgba(212,175,94,0.36), 0 0 0 6px rgba(212,175,94,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            ><Camera size={28} weight="bold" color="#0F0B1A" /></button>
            <div style={{ width: 52 }} />
          </div>
        </>
      )}

      {/* STAGE: POLAROID — handwriting editor */}
      {(stage === 'polaroid' || stage === 'saving') && photoDataUrl && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', paddingTop: 160, paddingBottom: 200, overflowY: 'auto' }}>
          <div style={{ transform: 'rotate(-1.5deg)', marginBottom: 18 }}>
            <Polaroid
              photoUrl={photoDataUrl}
              message={message || prompt}
              penColor={pen.hex}
              seed={seed}
              width={320}
              tape="washi"
              testId="capture-polaroid"
            />
          </div>
        </div>
      )}

      {/* STAGE: POLAROID — bottom drawer with input + pen palette + actions */}
      {stage === 'polaroid' && (
        <div style={{
          position: 'absolute', left: 0, right: 0,
          bottom: 'calc(env(safe-area-inset-bottom))',
          zIndex: 25,
          background: 'linear-gradient(180deg, rgba(10,11,26,0) 0%, rgba(10,11,26,0.88) 18%, rgba(10,11,26,0.96) 100%)',
          padding: '20px 16px 24px',
        }}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
            placeholder={prompt}
            maxLength={MAX_MESSAGE}
            data-testid="message-input"
            style={{
              width: '100%', padding: '12px 14px',
              borderRadius: 14, background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: '#fff', fontSize: 14, outline: 'none',
              fontFamily: 'Inter, sans-serif',
            }}
          />
          <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'right' }}>
            {message.length}/{MAX_MESSAGE}
          </div>
          {/* Pen palette */}
          <div data-testid="pen-palette" style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            {PEN_COLORS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPenId(p.id)}
                data-testid={`pen-${p.id}`}
                aria-label={`Pen colour ${p.label}`}
                title={p.label}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: p.hex, cursor: 'pointer',
                  border: penId === p.id ? '2px solid #D4AF5E' : '2px solid rgba(255,255,255,0.18)',
                  boxShadow: penId === p.id ? '0 0 0 3px rgba(212,175,94,0.25)' : 'inset 0 1px 2px rgba(0,0,0,0.3)',
                  transition: 'all 0.18s cubic-bezier(0.16,1,0.3,1)',
                }}
              />
            ))}
          </div>
          {/* Actions */}
          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={handleRetake}
              data-testid="retake-btn"
              style={{ flex: 1, height: 50, borderRadius: 14, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >Retake</button>
            <button
              type="button"
              onClick={handleSave}
              data-testid="save-btn"
              style={{ flex: 2, height: 50, borderRadius: 14, background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)', border: 'none', color: '#0F0B1A', fontWeight: 800, fontSize: 14, letterSpacing: '0.01em', cursor: 'pointer', boxShadow: '0 8px 26px rgba(212,175,94,0.32)' }}
            >Add to the Memory Wall</button>
          </div>
        </div>
      )}

      {/* STAGE: SUCCESS — composed editorial completion moment.
          Restructured 2026-05-13 (Craig flag): the success surface now
          renders its header INLINE (no longer absolute) so the
          "Added to the Memory Wall" h1 cannot crash into the Polaroid
          composition on narrow viewports. Polaroid width clamps to the
          viewport with breathing room; CTA row stacks the primary
          See-the-Wall full-width below Save/Share at ≤380px so no
          label clips and tactile press language stays intact. */}
      {stage === 'success' && savedSnap && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'flex-start',
          padding: '88px 16px calc(40px + env(safe-area-inset-bottom))',
          overflowY: 'auto',
        }}>
          {/* Inline success header — composed editorial rhythm */}
          <div data-testid="success-header" style={{
            textAlign: 'center', maxWidth: 440, padding: '0 8px',
            marginBottom: 'clamp(28px, 6vw, 40px)',
            width: '100%',
          }}>
            <p style={{
              fontSize: 10, fontWeight: 800,
              letterSpacing: '0.32em', textTransform: 'uppercase',
              color: 'rgba(212,175,94,0.78)', margin: 0,
            }}>✦ Memory Snap</p>
            <h1 style={{
              fontFamily: 'Georgia, serif',
              fontSize: 'clamp(19px, 5.6vw, 30px)',
              fontWeight: 500, letterSpacing: '-0.015em',
              lineHeight: 1.2,
              textShadow: '0 0 22px rgba(212,175,94,0.28)',
              margin: '8px 0 0',
              wordBreak: 'normal',
              overflowWrap: 'break-word',
              // text-wrap: balance keeps "Added to the Memory Wall"
              // visually balanced when it wraps, so "Wall" never sits
              // orphaned on its own line atop the polaroid below.
              // Falls back gracefully on older Android Chrome.
              textWrap: 'balance',
              padding: '0 4px',
            }}>
              <span aria-hidden style={{ marginRight: 6 }}>✨</span>
              Added to the Memory Wall
            </h1>
            <p style={{
              marginTop: 8,
              fontSize: 'clamp(12px, 3.4vw, 13px)',
              color: 'rgba(255,255,255,0.6)',
              fontStyle: 'italic',
              fontFamily: 'Georgia, serif',
            }}>
              Save your Polaroid, or take another.
            </p>
          </div>

          {/* Polaroid — responsive width clamps to viewport.
              The Polaroid component renders at an exact pixel width
              (tape, padding, message size all scale off it), so we
              compute `polaroidWidth` from window.innerWidth and pass
              it through. The outer wrapper matches that width so the
              rotated polaroid bounding box no longer crashes into
              the heading above on ≤360px screens. */}
          <div style={{
            width: polaroidWidth,
            maxWidth: '84vw',
            display: 'flex', justifyContent: 'center',
          }}>
            <div style={{
              animation: 'memorySnapJoin 1.4s cubic-bezier(0.16,1,0.3,1) forwards',
              transform: `rotate(${savedSnap.wall?.rotation || -1.5}deg) scale(${savedSnap.wall?.scale || 1}) translateY(0)`,
              transformOrigin: 'center top',
            }}>
              <Polaroid
                photoUrl={savedSnap.photo_url}
                message={savedSnap.message}
                penColor={savedSnap.pen_hex || pen.hex}
                seed={savedSnap.style_seed || seed}
                width={polaroidWidth}
                tape={savedSnap.wall?.tape || 'washi'}
                showCorners
                testId="success-polaroid"
              />
            </div>
          </div>

          {/* Primary actions — Save / Share / See the Wall.
              At ≤380px the See-the-Wall pill becomes full-width and
              sits below the Save/Share pair so labels never clip. */}
          <div
            data-testid="success-cta-row"
            style={{
              width: '100%', maxWidth: 360,
              display: 'flex', flexDirection: 'column',
              gap: 10,
              marginTop: 'clamp(28px, 6vw, 38px)',
            }}
          >
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                type="button"
                onClick={handleDownload}
                data-testid="download-btn"
                style={{
                  flex: 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  height: 48, padding: '0 14px', borderRadius: 14,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              ><DownloadSimple size={16} weight="bold" /> Save</button>
              <button
                type="button"
                onClick={handleShare}
                disabled={sharing}
                data-testid="share-btn"
                style={{
                  flex: 1,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  height: 48, padding: '0 14px', borderRadius: 14,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.16)',
                  color: '#fff', fontWeight: 700, fontSize: 13,
                  cursor: sharing ? 'default' : 'pointer', opacity: sharing ? 0.7 : 1,
                }}
              ><ShareNetwork size={16} weight="bold" /> Share</button>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/event/${slug}/memory-wall`)}
              data-testid="visit-wall-btn"
              style={{
                width: '100%',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                height: 52, padding: '0 22px', borderRadius: 14,
                background: 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)',
                color: '#0F0B1A', border: 'none',
                fontWeight: 800, fontSize: 14,
                letterSpacing: '0.01em',
                cursor: 'pointer',
                boxShadow: '0 8px 26px rgba(212,175,94,0.32)',
              }}
            >See the Wall <ArrowRight size={14} weight="bold" /></button>
          </div>

          {/* "Mine tonight" — the guest's own contributions tonight.
              Restructured for mobile: roomier gap (14px), generous
              outer padding so washi/pin overhang isn't clipped, no
              forced horizontal scroll while ≤3 fit comfortably. */}
          {mySnaps.length >= 2 && (
            <div data-testid="my-snaps-strip" style={{
              marginTop: 'clamp(34px, 8vw, 48px)',
              width: '100%', maxWidth: 440,
            }}>
              <p style={{
                fontSize: 10, fontWeight: 800,
                letterSpacing: '0.28em', textTransform: 'uppercase',
                color: 'rgba(212,175,94,0.65)',
                textAlign: 'center', margin: 0,
              }}>Mine tonight</p>
              <p style={{
                fontSize: 12, color: 'rgba(255,255,255,0.55)',
                textAlign: 'center', marginTop: 6,
                fontStyle: 'italic', fontFamily: 'Georgia, serif',
              }}>
                You've left {mySnaps.length} {mySnaps.length === 1 ? 'memory' : 'memories'} on the wall
              </p>
              <div style={{
                marginTop: 16,
                display: 'flex',
                gap: 14,
                justifyContent: 'center',
                overflowX: 'auto',
                paddingTop: 8,
                paddingBottom: 14,
                paddingLeft: 12,
                paddingRight: 12,
                scrollbarWidth: 'none',
              }}>
                {mySnaps.map((s, i) => (
                  <div
                    key={s.snap_id}
                    data-testid={`my-snap-thumb-${s.snap_id}`}
                    style={{
                      flex: '0 0 auto',
                      transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (1 + (i % 3))}deg)`,
                    }}
                  >
                    <Polaroid
                      photoUrl={s.photo_url}
                      message={s.message}
                      penColor={s.pen_hex || s.pen_color}
                      seed={s.style_seed || 1}
                      width={88}
                      tape={s.wall?.tape || 'washi'}
                      showCorners={false}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => { setSavedSnap(null); setMessage(''); setPhotoDataUrl(null); setStage('live'); }}
            data-testid="another-btn"
            style={{
              marginTop: 'clamp(20px, 5vw, 28px)',
              color: 'rgba(255,255,255,0.55)',
              background: 'transparent', border: 'none',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              padding: '8px 14px',
            }}
          >Take another snap</button>
        </div>
      )}

      <style>{`
        @keyframes memorySnapJoin {
          0%   { transform: rotate(0deg) scale(0.86) translateY(28px); opacity: 0; filter: blur(2px); }
          55%  { transform: rotate(-1deg) scale(1.04) translateY(-4px); opacity: 1; filter: blur(0); }
          100% { transform: rotate(-1.5deg) scale(1) translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default MemorySnapPage;
